import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Activity, ArrowLeft, Crown, Loader2, Save, RefreshCw, Users, TrendingUp, Sparkles, Headphones, AlertTriangle, Trash2, ChevronDown, ChevronRight, Handshake, Copy, Mail, Check, X, Ticket } from "lucide-react";

import { OpsTab } from "@/components/owner/OpsTab";
import { MobilityPanel } from "@/components/owner/MobilityPanel";
import { OriginalsPanel } from "@/components/OriginalsPanel";
import { SalesPanel } from "@/components/owner/SalesPanel";
import { RouteCatalogPanel } from "@/components/owner/RouteCatalogPanel";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import {
  getOwnerConfig,
  setDisplayPricing,
  setUsageLimits,
  
  getOwnerMetrics,
  listErrorLogs,
  listPricingChangeLog,
  clearPricingSyncLock,
  deleteErrorLog,
  setErrorLogResolved,
  archiveErrorLog,
  archiveResolvedErrorLogs,
  purgeExpiredErrorLogs,
  clearErrorLogs,
} from "@/lib/owner.functions";
import {
  listAffiliates,
  upsertAffiliate,
  toggleAffiliate,
  deleteAffiliate,
  getAffiliateStats,
  setCommissionStatus,
  listCommissions,
  runAutoApprove,
  exportCommissionsCsv,
  listFraudSignals,
  linkAffiliateAccount,
  unlinkAffiliateAccount,
  sendAffiliateInvite,
} from "@/lib/affiliates.functions";
import {
  listPartnershipRequests,
  approvePartnershipRequest,
  rejectPartnershipRequest,
  updatePartnershipNotes,
  deletePartnershipRequest,
} from "@/lib/partnerships.functions";
import { invalidatePricingLabels, fetchPricingLabels } from "@/lib/pricing-labels";
import { formatPricingEntry } from "@/lib/commercial-config";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/owner")({
  component: OwnerPage,
  head: () => ({
    meta: [
      { title: "Painel do Fundador · Routify" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Tab = "pricing" | "pricing-audit" | "ops" | "limits" | "metrics" | "affiliates" | "partnerships" | "errors" | "mobility" | "routes" | "originals" | "vendas";

type PricingEntry = { amount: number; currency: string; label: string; interval?: string };
type PricingMap = Record<string, PricingEntry>;
type LimitsShape = {
  anon: { monthly: number };
  free: { monthly: number };
  premium: { daily: number; monthly: number };
  pro: { daily: number; monthly: number };
};

const DEFAULT_KEYS = [
  "pro_monthly",
  "premium_monthly",
  "premium_yearly",
  "credits_pack_10",
  "credits_pack_30",
  "credits_pack_100",
] as const;

function defaultPricingEntry(key: string): PricingEntry {
  if (key.endsWith("yearly")) return { amount: 0, currency: "eur", label: "€0", interval: "year" };
  if (key.startsWith("credits_pack_")) return { amount: 0, currency: "eur", label: "€0" };
  return { amount: 0, currency: "eur", label: "€0", interval: "month" };
}

function OwnerPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isOwner, loading: roleLoading } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("pricing");

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || roleLoading) {
    return (
      <main className="mx-auto flex min-h-[70vh] w-full max-w-5xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isOwner) {
    return (
      <main className="mx-auto min-h-[70vh] w-full max-w-3xl px-4 py-16 text-center">
        <Crown className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-4 text-xl font-semibold">Acesso restrito ao fundador</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Este painel está reservado à conta com permissão de fundador.
        </p>
        <Link to="/account" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline">
          <ArrowLeft className="h-4 w-4" /> Voltar à minha conta
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-6">
      <div className="mb-5 flex items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Crown className="h-6 w-6 text-amber-500" /> Painel do Fundador
          </h1>
          <p className="text-sm text-muted-foreground">Configuração de preços, limites e métricas de negócio.</p>
        </div>
        <Link to="/admin" className="text-xs text-muted-foreground hover:underline">Painel de admin →</Link>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <TabButton active={tab === "pricing"} onClick={() => setTab("pricing")} icon={<Sparkles className="h-4 w-4" />} label="Preços" />
        <TabButton active={tab === "pricing-audit"} onClick={() => setTab("pricing-audit")} icon={<RefreshCw className="h-4 w-4" />} label="Auditoria de preços" />
        <TabButton active={tab === "ops"} onClick={() => setTab("ops")} icon={<Activity className="h-4 w-4" />} label="Operações" />
        <TabButton active={tab === "limits"} onClick={() => setTab("limits")} icon={<Users className="h-4 w-4" />} label="Limites de uso" />
        <TabButton active={tab === "metrics"} onClick={() => setTab("metrics")} icon={<TrendingUp className="h-4 w-4" />} label="Métricas" />
        <TabButton active={tab === "affiliates"} onClick={() => setTab("affiliates")} icon={<Handshake className="h-4 w-4" />} label="Afiliados" />
        <TabButton active={tab === "partnerships"} onClick={() => setTab("partnerships")} icon={<Mail className="h-4 w-4" />} label="Pedidos de parceria" />
        <TabButton active={tab === "mobility"} onClick={() => setTab("mobility")} icon={<Ticket className="h-4 w-4" />} label="Mobilidade oficial" />
        <TabButton active={tab === "routes"} onClick={() => setTab("routes")} icon={<Ticket className="h-4 w-4" />} label="Rotas curadas" />
        <TabButton active={tab === "originals"} onClick={() => setTab("originals")} icon={<Sparkles className="h-4 w-4" />} label="Originais" />
        <TabButton active={tab === "vendas"} onClick={() => setTab("vendas")} icon={<TrendingUp className="h-4 w-4" />} label="Vendas" />
        <TabButton active={tab === "errors"} onClick={() => setTab("errors")} icon={<AlertTriangle className="h-4 w-4" />} label="Erros" />
      </div>

      {tab === "pricing" ? <PricingTab /> : null}
      {tab === "limits" ? <LimitsTab /> : null}
      {tab === "metrics" ? <MetricsTab /> : null}
      {tab === "affiliates" ? <AffiliatesTab /> : null}
      {tab === "partnerships" ? <PartnershipsTab /> : null}
      {tab === "pricing-audit" ? <PricingAuditTab /> : null}
      {tab === "ops" ? <OpsTab /> : null}
      {tab === "mobility" ? <MobilityPanel /> : null}
      {tab === "routes" ? <RouteCatalogPanel /> : null}
      {tab === "originals" ? <OriginalsPanel /> : null}
      {tab === "vendas" ? <SalesPanel /> : null}
      {tab === "errors" ? <ErrorsTab /> : null}
    </main>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
        active ? "border-brand bg-brand text-brand-foreground" : "border-border bg-card text-foreground hover:bg-muted"
      }`}
    >
      {icon} {label}
    </button>
  );
}

/** Evento interno para a Auditoria de preços recarregar sem refresh manual. */
export const PRICING_AUDIT_REFRESH_EVENT = "routify:pricing-audit-refresh";
function notifyPricingAuditRefresh() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PRICING_AUDIT_REFRESH_EVENT));
}

function PricingTab() {
  const load = useServerFn(getOwnerConfig);
  const savePricing = useServerFn(setDisplayPricing);
  const [pricing, setPricing] = useState<PricingMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // O ambiente que manda é o do servidor (Stripe + configuração gravada).
  const [env, setEnv] = useState<string>(getStripeEnvironment());

  useEffect(() => {
    Promise.all([load(), fetchPricingLabels()])
      .then(([cfg, defaults]) => {
        const withDefaults: PricingMap = { ...defaults, ...(cfg.pricing ?? {}) };
        for (const k of DEFAULT_KEYS) if (!withDefaults[k]) withDefaults[k] = defaultPricingEntry(k);
        setPricing(withDefaults);
        if (cfg.environment) setEnv(cfg.environment);
      })
      .catch((e) => toast.error(e.message ?? "Erro a carregar preços"))
      .finally(() => setLoading(false));
  }, [load]);

  function updateEntry(key: string, patch: Partial<PricingEntry>) {
    setPricing((prev) => {
      const next = { ...prev[key], ...patch };
      return { ...prev, [key]: { ...next, label: formatPricingEntry(next) } };
    });
  }

  /** ÚNICO fluxo: validar -> sincronizar Stripe -> gravar config -> auditar. */
  async function handleSave() {
    setSaving(true);
    try {
      const res = await savePricing({ data: pricing });
      if (!res.ok) {
        toast.error(res.error ?? "Não foi possível sincronizar com o Stripe. Nada foi gravado.");
        notifyPricingAuditRefresh();
        return;
      }
      invalidatePricingLabels();
      // Mostrar sempre o que o servidor confirmou, não o estado local.
      const confirmed = (res.pricing ?? null) as PricingMap | null;
      if (confirmed) {
        setPricing((prev) => ({ ...prev, ...confirmed }));
      } else {
        const cfg = await load();
        if (cfg.pricing) setPricing((prev) => ({ ...prev, ...(cfg.pricing as PricingMap) }));
      }
      await fetchPricingLabels();
      notifyPricingAuditRefresh();
      const synced = res.synced?.length ?? 0;
      toast.success(
        synced
          ? `Preços guardados e sincronizados no Stripe (${synced} ${synced === 1 ? "preço sincronizado" : "preços sincronizados"}).`
          : "Preços guardados (nenhuma alteração a sincronizar no Stripe).",
      );
    } catch (e: any) {
      toast.error(e.message ?? "Erro a guardar");
    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
        <p className="text-sm text-muted-foreground">
          Usa apenas <strong>Guardar e sincronizar preços</strong>. A alteração é validada e sincronizada no Stripe antes
          de ficar visível na aplicação. Se alguma etapa falhar, o preço anterior permanece ativo e o erro fica auditado.
        </p>

        <p className="mt-2 text-xs text-muted-foreground">
          Ambiente Stripe atual: <strong>{env}</strong> — estas alterações afetam apenas a configuração{" "}
          <strong>{env}</strong>. Os preços do outro ambiente ficam intactos.
        </p>
      </div>

      {Object.entries(pricing).map(([key, entry]) => (
        <div key={key} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">lookup_key</div>
              <div className="font-mono text-sm font-semibold">{key}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Etiqueta na app</div>
              <div className="text-lg font-semibold">{formatPricingEntry(entry)}</div>
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="text-xs">
              Valor (cêntimos)
              <input
                type="number"
                min={0}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={entry.amount}
                onChange={(e) => updateEntry(key, { amount: Number(e.target.value) || 0 })}
              />
            </label>
            <label className="text-xs">
              Moeda
              <input
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={entry.currency}
                onChange={(e) => updateEntry(key, { currency: e.target.value.toLowerCase() })}
              />
            </label>
            <label className="text-xs">
              Intervalo (opcional)
              <select
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={entry.interval ?? ""}
                onChange={(e) => updateEntry(key, { interval: e.target.value || undefined })}
              >
                <option value="">—</option>
                <option value="month">month</option>
                <option value="year">year</option>
                <option value="week">week</option>
                <option value="day">day</option>
              </select>
            </label>
          </div>

        </div>
      ))}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground hover:opacity-90 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar e sincronizar preços
        </button>
      </div>

    </section>
  );
}

type PricingAuditEntryView = {
  id: string;
  lookupKey: string;
  environment: string;
  fromAmount: number | null;
  toAmount: number;
  currency: string;
  interval: string | null;
  stripePriceId: string | null;
  status: string;
  errorMessage: string | null;
  actor: string;
  createdAt: string;
};

const AUDIT_STATUS_LABEL: Record<string, string> = {
  synced: "Sincronizado",
  failed: "Falhou",
  rolled_back: "Revertido",
  rollback_failed: "Reversão falhou",
  locked: "Bloqueado",
};

function auditStatusClass(status: string) {
  if (status === "synced") return "bg-emerald-500/10 text-emerald-600 border-emerald-500/30";
  if (status === "rolled_back") return "bg-amber-500/10 text-amber-600 border-amber-500/30";
  return "bg-destructive/10 text-destructive border-destructive/30";
}

function formatCents(amount: number | null, currency: string) {
  if (amount == null) return "—";
  const value = (amount / 100).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency.toLowerCase() === "eur" ? `€${value}` : `${value} ${currency.toUpperCase()}`;
}

function PricingAuditTab() {
  const load = useServerFn(listPricingChangeLog);
  const clearLock = useServerFn(clearPricingSyncLock);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<PricingAuditEntryView[]>([]);
  const [lock, setLock] = useState<{ reason: string; at: string } | null>(null);

  const refresh = () => {
    setLoading(true);
    load({})
      .then((res: any) => {
        setEntries(res.entries ?? []);
        setLock(res.lock ?? null);
      })
      .catch((e: any) => toast.error(e.message ?? "Erro a carregar auditoria"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    const onRefresh = () => refresh();
    window.addEventListener(PRICING_AUDIT_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(PRICING_AUDIT_REFRESH_EVENT, onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Histórico completo das alterações de preços: quem alterou, valor antes/depois, ambiente, ID do preço no Stripe e resultado.
        </p>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>

      {lock ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="font-semibold text-destructive">Sincronização bloqueada — checkouts desativados</p>
          <p className="mt-1 text-muted-foreground">{lock.reason}</p>
          <p className="mt-1 text-xs text-muted-foreground">Desde {lock.at ? new Date(lock.at).toLocaleString("pt-PT") : "—"}</p>
          <button
            type="button"
            onClick={() =>
              clearLock({})
                .then(() => { toast.success("Bloqueio removido"); refresh(); })
                .catch((e: any) => toast.error(e.message ?? "Erro a remover bloqueio"))
            }
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-xs font-semibold text-white"
          >
            Já corrigi no Stripe — desbloquear
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> A carregar…</div>
      ) : entries.length === 0 ? (
        <p className="rounded-xl border border-border p-6 text-sm text-muted-foreground">Ainda não há alterações de preços registadas.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Utilizador</th>
                <th className="px-3 py-2">Produto</th>
                <th className="px-3 py-2">Antes → Depois</th>
                <th className="px-3 py-2">Ambiente</th>
                <th className="px-3 py-2">Stripe Price ID</th>
                <th className="px-3 py-2">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(e.createdAt).toLocaleString("pt-PT")}</td>
                  <td className="px-3 py-2">{e.actor}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.lookupKey}{e.interval ? ` · ${e.interval}` : ""}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatCents(e.fromAmount, e.currency)} → <strong>{formatCents(e.toAmount, e.currency)}</strong>
                  </td>
                  <td className="px-3 py-2">{e.environment}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.stripePriceId ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${auditStatusClass(e.status)}`}>
                      {AUDIT_STATUS_LABEL[e.status] ?? e.status}
                    </span>
                    {e.errorMessage ? <p className="mt-1 max-w-[280px] text-xs text-muted-foreground">{e.errorMessage}</p> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LimitsTab() {
  const load = useServerFn(getOwnerConfig);
  const save = useServerFn(setUsageLimits);
  const [limits, setLimits] = useState<LimitsShape>({
    anon: { monthly: 2 },
    free: { monthly: 5 },
    premium: { daily: 10, monthly: 15 },
    pro: { daily: 5, monthly: 50 },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load()
      .then((cfg) => {
        if (cfg.limits && Object.keys(cfg.limits).length) {
          setLimits({
            anon: { monthly: cfg.limits.anon?.monthly ?? 2 },
            free: { monthly: cfg.limits.free?.monthly ?? 5 },
            premium: {
              daily: cfg.limits.premium?.daily ?? 10,
              monthly: cfg.limits.premium?.monthly ?? 15,
            },
            pro: {
              daily: (cfg.limits as any).pro?.daily ?? 5,
              monthly: (cfg.limits as any).pro?.monthly ?? 50,
            },
          });
        }
      })
      .catch((e) => toast.error(e.message ?? "Erro a carregar limites"))
      .finally(() => setLoading(false));
  }, [load]);

  async function handleSave() {
    setSaving(true);
    try {
      await save({ data: limits });
      invalidatePricingLabels();
      toast.success("Limites atualizados e sincronizados com as descrições.");
    } catch (e: any) {
      toast.error(e.message ?? "Erro a guardar");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold">Anónimos (sem conta)</h3>
        <NumberField label="Rotas por mês" value={limits.anon.monthly} onChange={(v) => setLimits({ ...limits, anon: { monthly: v } })} />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold">Utilizadores grátis</h3>
        <NumberField label="Rotas por mês" value={limits.free.monthly} onChange={(v) => setLimits({ ...limits, free: { monthly: v } })} />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold">Premium (rotas clássicas)</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberField label="Rotas por dia" value={limits.premium.daily} onChange={(v) => setLimits({ ...limits, premium: { ...limits.premium, daily: v } })} />
          <NumberField label="Rotas por mês" value={limits.premium.monthly} onChange={(v) => setLimits({ ...limits, premium: { ...limits.premium, monthly: v } })} />
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold">Routify Pro (roteiros multi-dia)</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberField label="Rotas por dia" value={limits.pro.daily} onChange={(v) => setLimits({ ...limits, pro: { ...limits.pro, daily: v } })} />
          <NumberField label="Rotas por mês" value={limits.pro.monthly} onChange={(v) => setLimits({ ...limits, pro: { ...limits.pro, monthly: v } })} />
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground hover:opacity-90 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar limites
        </button>
      </div>
    </section>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block text-xs">
      {label}
      <input
        type="number"
        min={0}
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
      />
    </label>
  );
}

function MetricsTab() {
  const load = useServerFn(getOwnerMetrics);
  const [m, setM] = useState<Awaited<ReturnType<typeof getOwnerMetrics>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load().then(setM).catch((e) => toast.error(e.message ?? "Erro a carregar métricas")).finally(() => setLoading(false));
  }, [load]);

  const cards = useMemo(() => {
    if (!m) return [];
    return [
      { label: "Utilizadores registados", value: m.totalUsers.toLocaleString("pt-PT"), icon: <Users className="h-4 w-4" /> },
      { label: "Subscrições ativas", value: m.activeSubscriptions.toLocaleString("pt-PT"), icon: <Crown className="h-4 w-4" /> },
      { label: "MRR estimado", value: `€${(m.mrrCents / 100).toFixed(2)}`, icon: <TrendingUp className="h-4 w-4" /> },
      { label: "Rotas geradas este mês", value: m.itinerariesThisMonth.toLocaleString("pt-PT"), icon: <Sparkles className="h-4 w-4" /> },
      { label: "Pedidos concierge (total)", value: m.conciergeTotal.toLocaleString("pt-PT"), icon: <Headphones className="h-4 w-4" /> },
      { label: "Receita concierge (mês)", value: `€${(m.conciergeThisMonthRevenueCents / 100).toFixed(2)}`, icon: <TrendingUp className="h-4 w-4" /> },
    ];
  }, [m]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (!m) return null;

  return (
    <section className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">{c.icon}{c.label}</div>
            <div className="mt-2 text-2xl font-bold">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold">Subscrições por plano</h3>
        <ul className="mt-3 space-y-1 text-sm">
          {Object.entries(m.subscriptionsByPrice).length === 0 ? (
            <li className="text-muted-foreground">Sem subscrições ativas.</li>
          ) : (
            Object.entries(m.subscriptionsByPrice)
              .sort((a, b) => b[1] - a[1])
              .map(([key, count]) => (
                <li key={key} className="flex justify-between border-b border-border/60 py-1 last:border-none">
                  <span className="font-mono text-xs">{key}</span>
                  <span className="font-semibold">{count}</span>
                </li>
              ))
          )}
        </ul>
      </div>
    </section>
  );
}

type ErrorRow = {
  id: string;
  created_at: string;
  source: string;
  message: string;
  stack: string | null;
  url: string | null;
  user_agent: string | null;
  user_id: string | null;
  status: number | null;
  context: any;
  resolved_at?: string | null;
  archived_at?: string | null;
};

function ErrorsTab() {
  const load = useServerFn(listErrorLogs);
  const del = useServerFn(deleteErrorLog);
  const clear = useServerFn(clearErrorLogs);
  const setResolved = useServerFn(setErrorLogResolved);
  const archiveOne = useServerFn(archiveErrorLog);
  const archiveResolved = useServerFn(archiveResolvedErrorLogs);
  const purgeExpired = useServerFn(purgeExpiredErrorLogs);
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [last24, setLast24] = useState(0);
  const [source, setSource] = useState<"all" | "client" | "server" | "serverfn" | "webhook" | "cron">("all");
  const [state, setState] = useState<"active" | "resolved" | "archived" | "all">("active");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  async function refresh() {
    setLoading(true);
    try {
      const res = await load({
        data: {
          source,
          state,
          limit: 100,
          ...(from ? { from: new Date(from).toISOString() } : {}),
          ...(to ? { to: new Date(`${to}T23:59:59`).toISOString() } : {}),
        },
      });
      setRows(res.rows as ErrorRow[]);
      setTotal(res.totalCount);
      setLast24(res.last24h);
      setActiveCount(res.activeCount);
    } catch (e: any) {
      toast.error(e.message ?? "Erro a carregar logs");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, state, from, to]);

  async function handleResolve(id: string, resolved: boolean) {
    try {
      await setResolved({ data: { id, resolved } });
      toast.success(resolved ? "Erro marcado como resolvido" : "Erro reaberto");
      await refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a atualizar estado");
    }
  }

  async function handleArchive(id: string) {
    try {
      await archiveOne({ data: { id } });
      toast.success("Erro arquivado (mantido na base de dados)");
      await refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a arquivar");
    }
  }

  async function handleArchiveResolved() {
    if (!confirm("Arquivar todos os erros já resolvidos? Deixam de aparecer na vista normal mas continuam guardados.")) return;
    try {
      const r: any = await archiveResolved({});
      toast.success(`${r.archived} erro(s) arquivado(s)`);
      await refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a arquivar");
    }
  }

  async function handlePurgeExpired() {
    if (!confirm("Aplicar a retenção automática? Apaga apenas erros já triados com mais de 30 dias. Auditoria de preços, pagamentos e webhooks nunca são apagados.")) return;
    try {
      const r: any = await purgeExpired({});
      toast.success(`${r.removed} registo(s) operacional(is) removido(s)`);
      await refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha na retenção");
    }
  }

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleDelete(id: string) {
    try {
      await del({ data: { id } });
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Erro apagado");
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao apagar");
    }
  }

  async function handleClearAll() {
    if (!confirm("Apagar TODOS os erros registados? Esta ação é definitiva.")) return;
    try {
      await clear({ data: {} });
      await refresh();
      toast.success("Todos os erros apagados");
    } catch (e: any) {
      toast.error(e.message ?? "Falha a limpar");
    }
  }

  async function handleClearOld() {
    if (!confirm("Apagar erros com mais de 7 dias?")) return;
    try {
      await clear({ data: { olderThanDays: 7 } });
      await refresh();
      toast.success("Erros antigos apagados");
    } catch (e: any) {
      toast.error(e.message ?? "Falha a limpar");
    }
  }

  const sourceBadgeClass = (s: string) => {
    switch (s) {
      case "client": return "bg-blue-100 text-blue-800";
      case "server": return "bg-red-100 text-red-800";
      case "serverfn": return "bg-orange-100 text-orange-800";
      case "webhook": return "bg-purple-100 text-purple-800";
      case "cron": return "bg-gray-100 text-gray-800";
      default: return "bg-muted text-foreground";
    }
  };

  return (
    <section className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">Total registado</div>
          <div className="mt-2 text-2xl font-bold">{total.toLocaleString("pt-PT")}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">Últimas 24h</div>
          <div className="mt-2 text-2xl font-bold">{last24.toLocaleString("pt-PT")}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">Ativos (por resolver)</div>
          <div className="mt-2 text-2xl font-bold">{activeCount.toLocaleString("pt-PT")}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as any)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="all">Todas as origens</option>
          <option value="client">Browser (client)</option>
          <option value="server">Servidor (SSR)</option>
          <option value="serverfn">Server functions</option>
          <option value="webhook">Webhooks</option>
          <option value="cron">Cron</option>
        </select>
        <select
          value={state}
          onChange={(e) => setState(e.target.value as any)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="active">Apenas ativos</option>
          <option value="resolved">Resolvidos</option>
          <option value="archived">Arquivados</option>
          <option value="all">Todos</option>
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          aria-label="Data inicial"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          aria-label="Data final"
        />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" /> Atualizar
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleArchiveResolved}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
          >
            Arquivar resolvidos
          </button>
          <button
            type="button"
            onClick={handlePurgeExpired}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
          >
            Aplicar retenção (30 dias)
          </button>
          <button
            type="button"
            onClick={handleClearOld}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
          >
            <Trash2 className="h-4 w-4" /> Limpar &gt; 7 dias
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className="inline-flex items-center gap-2 rounded-full border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 hover:bg-red-100"
          >
            <Trash2 className="h-4 w-4" /> Apagar tudo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Sem erros registados 🎉
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const isOpen = expanded.has(r.id);
            return (
              <div key={r.id} className="rounded-xl border border-border bg-card">
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left"
                >
                  {isOpen ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${sourceBadgeClass(r.source)}`}>
                        {r.source}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("pt-PT")}
                      </span>
                      {r.status ? <span className="text-xs text-muted-foreground">HTTP {r.status}</span> : null}
                    </div>
                    <div className="mt-1 truncate text-sm font-medium">{r.message}</div>
                    {r.url ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{r.url}</div> : null}
                  </div>
                  <span className="flex shrink-0 items-center gap-1">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); handleResolve(r.id, !r.resolved_at); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleResolve(r.id, !r.resolved_at); } }}
                      className="cursor-pointer rounded-full border border-border px-2 py-1 text-[11px] font-semibold hover:bg-muted"
                    >
                      {r.resolved_at ? "Reabrir" : "Resolver"}
                    </span>
                    {r.archived_at ? (
                      <span className="rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground">arquivado</span>
                    ) : (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handleArchive(r.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleArchive(r.id); } }}
                        className="cursor-pointer rounded-full border border-border px-2 py-1 text-[11px] font-semibold hover:bg-muted"
                      >
                        Arquivar
                      </span>
                    )}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleDelete(r.id); } }}
                      className="cursor-pointer rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-red-600"
                      aria-label="Apagar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </span>
                  </span>
                </button>
                {isOpen ? (
                  <div className="border-t border-border px-4 py-3 text-xs">
                    {r.user_id ? <div><span className="text-muted-foreground">User: </span><span className="font-mono">{r.user_id}</span></div> : null}
                    {r.user_agent ? <div className="mt-1"><span className="text-muted-foreground">UA: </span>{r.user_agent}</div> : null}
                    {r.stack ? (
                      <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-muted p-3 text-[11px] leading-snug">{r.stack}</pre>
                    ) : null}
                    {r.context ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-muted-foreground">Contexto</summary>
                        <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-muted p-3 text-[11px]">{JSON.stringify(r.context, null, 2)}</pre>
                      </details>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Afiliados
// ============================================================================

type AffiliateRow = {
  id: string;
  name: string;
  email: string;
  code: string;
  ref_slug: string;
  discount_percent: number;
  commission_percent: number;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
  notes: string | null;
  stripe_coupon_id_sandbox: string | null;
  stripe_promotion_code_id_sandbox: string | null;
  stripe_coupon_id_live: string | null;
  stripe_promotion_code_id_live: string | null;
  created_at: string;
  user_id: string | null;
};

type AffiliateForm = {
  id?: string;
  name: string;
  email: string;
  code: string;
  ref_slug: string;
  discount_percent: number;
  commission_percent: number;
  starts_at: string;
  ends_at: string;
  active: boolean;
  notes: string;
  syncStripe: "sandbox" | "live" | "both" | "none";
};

const EMPTY_FORM: AffiliateForm = {
  name: "",
  email: "",
  code: "",
  ref_slug: "",
  discount_percent: 15,
  commission_percent: 20,
  starts_at: "",
  ends_at: "",
  active: true,
  notes: "",
  syncStripe: "both",
};

function AffiliatesTab() {
  const list = useServerFn(listAffiliates);
  const upsert = useServerFn(upsertAffiliate);
  const toggle = useServerFn(toggleAffiliate);
  const remove = useServerFn(deleteAffiliate);
  const link = useServerFn(linkAffiliateAccount);
  const unlink = useServerFn(unlinkAffiliateAccount);
  const invite = useServerFn(sendAffiliateInvite);

  const [rows, setRows] = useState<AffiliateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<AffiliateForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const data = await list();
      setRows(data as AffiliateRow[]);
    } catch (e: any) {
      toast.error(e.message ?? "Erro a carregar afiliados");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  function startNew() {
    setForm({ ...EMPTY_FORM });
  }

  function editRow(r: AffiliateRow) {
    setForm({
      id: r.id,
      name: r.name,
      email: r.email,
      code: r.code,
      ref_slug: r.ref_slug,
      discount_percent: r.discount_percent,
      commission_percent: r.commission_percent,
      starts_at: r.starts_at ? r.starts_at.slice(0, 10) : "",
      ends_at: r.ends_at ? r.ends_at.slice(0, 10) : "",
      active: r.active,
      notes: r.notes ?? "",
      syncStripe: "both",
    });
  }

  async function handleSave() {
    if (!form) return;
    setSaving(true);
    try {
      const res = await upsert({
        data: {
          ...(form.id ? { id: form.id } : {}),
          name: form.name.trim(),
          email: form.email.trim(),
          code: form.code.trim().toUpperCase(),
          ref_slug: form.ref_slug.trim().toUpperCase(),
          discount_percent: form.discount_percent,
          commission_percent: form.commission_percent,
          starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
          ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
          active: form.active,
          notes: form.notes.trim() || null,
          syncStripe: form.syncStripe,
        },
      });
      if (!res.ok) {
        toast.error(res.error ?? "Falha a guardar afiliado");
      } else {
        toast.success("Afiliado guardado");
        setForm(null);
        refresh();
      }
    } catch (e: any) {
      toast.error(e.message ?? "Erro a guardar afiliado");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(r: AffiliateRow) {
    try {
      await toggle({ data: { id: r.id, active: !r.active } });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, active: !r.active } : x)));
    } catch (e: any) {
      toast.error(e.message ?? "Falha a alternar estado");
    }
  }

  async function handleDelete(r: AffiliateRow) {
    if (!confirm(`Eliminar afiliado "${r.name}" (${r.code})?`)) return;
    try {
      const res = await remove({ data: { id: r.id } });
      if (!res.ok) {
        toast.error(res.error ?? "Falha a eliminar");
        return;
      }
      toast.success("Afiliado eliminado");
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e: any) {
      toast.error(e.message ?? "Falha a eliminar");
    }
  }

  function copyLink(slug: string) {
    const url = `${window.location.origin}/?ref=${encodeURIComponent(slug)}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copiado", { description: url }),
      () => toast.error("Não foi possível copiar"),
    );
  }

  function copyDashboardLink() {
    const url = `${window.location.origin}/affiliate`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link do painel copiado", { description: url }),
      () => toast.error("Não foi possível copiar"),
    );
  }

  async function handleLink(r: AffiliateRow) {
    try {
      const res = await link({ data: { affiliateId: r.id } });
      if (!res.ok) {
        if (res.error === "no_account") toast.error("Sem conta com esse email — envia convite.");
        else toast.error(res.error ?? "Falha a associar conta");
        return;
      }
      toast.success(res.alreadyLinked ? "Já estava associado" : "Conta associada");
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a associar conta");
    }
  }

  async function handleUnlink(r: AffiliateRow) {
    if (!confirm(`Desassociar a conta de "${r.name}"?`)) return;
    try {
      const res = await unlink({ data: { affiliateId: r.id } });
      if (!res.ok) return toast.error(res.error ?? "Falha a desassociar");
      toast.success("Conta desassociada");
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a desassociar");
    }
  }

  async function handleInvite(r: AffiliateRow) {
    try {
      const res = await invite({ data: { affiliateId: r.id } });
      if (!res.ok) return toast.error(res.error ?? "Falha a enviar convite");
      toast.success(res.mode === "magiclink" ? "Link mágico enviado" : "Convite enviado");
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a enviar convite");
    }
  }


  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Programa de afiliados</h3>
          <p className="text-xs text-muted-foreground">
            Cada afiliado tem um link <code className="font-mono">?ref=SLUG</code> e um código promocional Stripe.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4" /> Atualizar
          </button>
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Handshake className="h-4 w-4" /> Novo afiliado
          </button>
        </div>
      </div>

      {form ? (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs">
              Nome
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Email
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Código promocional (Stripe)
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="COPAO15" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm uppercase" />
            </label>
            <label className="text-xs">
              Slug do link (?ref=)
              <input value={form.ref_slug} onChange={(e) => setForm({ ...form, ref_slug: e.target.value.toUpperCase() })} placeholder="COPAO" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm uppercase" />
            </label>
            <label className="text-xs">
              Desconto ao cliente (%)
              <input type="number" min={0} max={100} value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Comissão para o afiliado (%)
              <input type="number" min={0} max={100} value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Início (opcional)
              <input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Fim (opcional)
              <input type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-xs">
              Sincronizar com Stripe
              <select value={form.syncStripe} onChange={(e) => setForm({ ...form, syncStripe: e.target.value as any })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <option value="both">Sandbox + Live</option>
                <option value="sandbox">Só sandbox</option>
                <option value="live">Só live</option>
                <option value="none">Não sincronizar</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Ativo
            </label>
            <label className="text-xs sm:col-span-2">
              Notas
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
            </button>
            <button type="button" onClick={() => setForm(null)} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-muted">
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Ainda não tens afiliados. Cria o primeiro para gerar um link de partilha.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{r.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.active ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
                      {r.active ? "Ativo" : "Inativo"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.user_id ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`} title={r.user_id ? "Conta associada" : "Sem conta associada"}>
                      {r.user_id ? "Conta ligada" : "Sem conta"}
                    </span>
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{r.code}</code>
                    <span className="text-xs text-muted-foreground">
                      −{r.discount_percent}% · comissão {r.commission_percent}%
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{r.email}</div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button type="button" onClick={() => copyLink(r.ref_slug)} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted">
                    <Copy className="h-3.5 w-3.5" /> Link ?ref
                  </button>
                  <button type="button" onClick={copyDashboardLink} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted" title="Link para o painel do afiliado">
                    <Copy className="h-3.5 w-3.5" /> Link painel
                  </button>
                  {r.user_id ? (
                    <button type="button" onClick={() => handleUnlink(r)} className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted">
                      Desassociar
                    </button>
                  ) : (
                    <button type="button" onClick={() => handleLink(r)} className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs text-blue-800 hover:bg-blue-100">
                      Associar conta
                    </button>
                  )}
                  <button type="button" onClick={() => handleInvite(r)} className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted" title={r.user_id ? "Enviar link mágico" : "Enviar convite por email"}>
                    {r.user_id ? "Enviar link" : "Convidar"}
                  </button>
                  <button type="button" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)} className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted">
                    {expandedId === r.id ? "Fechar" : "Ver stats"}
                  </button>
                  <button type="button" onClick={() => editRow(r)} className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted">Editar</button>
                  <button type="button" onClick={() => handleToggle(r)} className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted">
                    {r.active ? "Desativar" : "Ativar"}
                  </button>
                  <button type="button" onClick={() => handleDelete(r)} className="rounded-full border border-red-300 bg-red-50 px-3 py-1.5 text-xs text-red-700 hover:bg-red-100">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {expandedId === r.id ? <AffiliateStatsRow affiliateId={r.id} /> : null}
            </div>
          ))}
        </div>
      )}

      <CommissionsPanel />
    </section>
  );
}

function AffiliateStatsRow({ affiliateId }: { affiliateId: string }) {
  const stats = useServerFn(getAffiliateStats);
  const [data, setData] = useState<{ clicks: number; signups: number; purchases: number; pendingCents: number; paidCents: number } | null>(null);

  useEffect(() => {
    stats({ data: { id: affiliateId } }).then(setData).catch(() => setData(null));
  }, [affiliateId, stats]);

  const cells = [
    { label: "Cliques", value: data?.clicks ?? "–" },
    { label: "Registos", value: data?.signups ?? "–" },
    { label: "Compras", value: data?.purchases ?? "–" },
    { label: "Comissão pendente", value: data ? `€${(data.pendingCents / 100).toFixed(2)}` : "–" },
    { label: "Comissão paga", value: data ? `€${(data.paidCents / 100).toFixed(2)}` : "–" },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 border-t border-border px-4 py-3 sm:grid-cols-5">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg bg-muted/50 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.label}</div>
          <div className="mt-0.5 text-sm font-semibold">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

type CommissionRow = {
  id: string;
  created_at: string;
  gross_cents: number;
  commission_cents: number;
  currency: string;
  status: "pending" | "validating" | "approved" | "paid" | "canceled" | "void";
  hold_until: string | null;
  self_referral: boolean | null;
  cancel_reason: string | null;
  stripe_session_id: string | null;
  stripe_invoice_id: string | null;
  affiliates: { name: string; code: string } | null;
};

function CommissionsPanel() {
  const load = useServerFn(listCommissions);
  const setStatus = useServerFn(setCommissionStatus);
  const autoApprove = useServerFn(runAutoApprove);
  const exportCsv = useServerFn(exportCommissionsCsv);
  const fraud = useServerFn(listFraudSignals);
  const [rows, setRows] = useState<CommissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState<{ selfReferrals: any[]; velocity: any[] } | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [data, s] = await Promise.all([
        load({ data: {} }),
        fraud().catch(() => ({ selfReferrals: [], velocity: [] })),
      ]);
      setRows(data as CommissionRow[]);
      setSignals(s as any);
    } catch (e: any) {
      toast.error(e.message ?? "Erro a carregar comissões");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function markStatus(id: string, status: CommissionRow["status"]) {
    try {
      const res = await setStatus({ data: { id, status } });
      if ((res as any)?.ok === false) throw new Error((res as any).error);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e: any) {
      toast.error(e.message ?? "Falha a atualizar comissão");
    }
  }

  async function handleAutoApprove() {
    try {
      const res: any = await autoApprove();
      if (res?.ok === false) throw new Error(res.error);
      toast.success(`${res?.approved ?? 0} comissões aprovadas`);
      await refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Falha a executar auto-aprovação");
    }
  }

  async function handleExport() {
    try {
      const res: any = await exportCsv({ data: {} });
      if (res?.ok === false) throw new Error(res.error);
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comissoes-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message ?? "Falha a exportar");
    }
  }

  const badge = (s: string) => {
    switch (s) {
      case "pending": return "bg-amber-100 text-amber-800";
      case "validating": return "bg-orange-100 text-orange-800";
      case "approved": return "bg-blue-100 text-blue-800";
      case "paid": return "bg-emerald-100 text-emerald-800";
      case "canceled": return "bg-red-100 text-red-800";
      case "void": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-foreground";
    }
  };

  const fraudCount = (signals?.selfReferrals.length ?? 0) + (signals?.velocity.length ?? 0);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Comissões</h3>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleAutoApprove} className="rounded-full border border-border px-3 py-1 text-xs hover:bg-muted">
            Aprovar após 14 dias
          </button>
          <button type="button" onClick={handleExport} className="rounded-full border border-border px-3 py-1 text-xs hover:bg-muted">
            Exportar CSV
          </button>
          <button type="button" onClick={refresh} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
            <RefreshCw className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>
      </div>

      {fraudCount > 0 ? (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <strong>Alertas:</strong> {signals?.selfReferrals.length ?? 0} auto-referências ·{" "}
          {signals?.velocity.length ?? 0} compras rápidas do mesmo cliente (últimas 24h).
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ainda não há comissões registadas.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{r.affiliates?.name ?? "—"}</span>
                  {r.affiliates?.code ? <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{r.affiliates.code}</code> : null}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badge(r.status)}`}>{r.status}</span>
                  {r.self_referral ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-800">self-ref</span> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("pt-PT")} · Bruto {(r.gross_cents / 100).toFixed(2)} {r.currency.toUpperCase()} · Comissão {(r.commission_cents / 100).toFixed(2)} {r.currency.toUpperCase()}
                  {r.hold_until && r.status === "pending" ? <> · liberta {new Date(r.hold_until).toLocaleDateString("pt-PT")}</> : null}
                  {r.cancel_reason ? <> · motivo: {r.cancel_reason}</> : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(["approved", "paid", "canceled", "void"] as const).map((s) => (
                  <button key={s} type="button" onClick={() => markStatus(r.id, s)} disabled={r.status === s} className="rounded-full border border-border px-2.5 py-1 text-[11px] hover:bg-muted disabled:opacity-40">
                    {s}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// -------------- Partnerships Tab --------------

type PartnershipRow = {
  id: string;
  name: string;
  email: string;
  country: string | null;
  social_networks: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  youtube_url: string | null;
  followers_approx: string | null;
  niche: string | null;
  reason: string | null;
  avg_views_per_post: string | null;
  avg_views_stories: string | null;
  audience_country: string | null;
  audience_age_range: string | null;
  media_kit_url: string | null;
  past_partnerships: string | null;
  promotion_plan: string | null;
  accepted_terms: boolean | null;
  status: string;
  admin_notes: string | null;
  reviewed_at: string | null;
  affiliate_id: string | null;
  created_at: string;
};

function PartnershipsTab() {
  const load = useServerFn(listPartnershipRequests);
  const approveFn = useServerFn(approvePartnershipRequest);
  const rejectFn = useServerFn(rejectPartnershipRequest);
  const notesFn = useServerFn(updatePartnershipNotes);
  const deleteFn = useServerFn(deletePartnershipRequest);
  const [rows, setRows] = useState<PartnershipRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh(status = filter) {
    setLoading(true);
    try {
      const data = await load({ data: { status } });
      setRows(data as PartnershipRow[]);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro a carregar pedidos");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(filter); }, [filter]);

  async function handleApprove(row: PartnershipRow) {
    const codeInput = window.prompt("Código promocional (deixa vazio para gerar a partir do nome):", "");
    const discountStr = window.prompt("Desconto para clientes (%):", "10");
    const commissionStr = window.prompt("Comissão do afiliado (%):", "20");
    if (discountStr === null || commissionStr === null) return;
    const discount = Math.max(0, Math.min(100, Number(discountStr) || 0));
    const commission = Math.max(0, Math.min(100, Number(commissionStr) || 0));
    setBusyId(row.id);
    try {
      const r = await approveFn({
        data: {
          id: row.id,
          code: codeInput?.trim() ? codeInput.trim() : undefined,
          discountPercent: discount,
          commissionPercent: commission,
          syncStripe: "both",
        },
      });
      if (r.ok) {
        toast.success(`Parceria aprovada · código ${r.code}`);
        await refresh();
      } else {
        toast.error(r.error ?? "Erro a aprovar");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Erro a aprovar");
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(row: PartnershipRow) {
    if (!window.confirm(`Rejeitar o pedido de ${row.name}?`)) return;
    setBusyId(row.id);
    try {
      const r = await rejectFn({ data: { id: row.id } });
      if (r.ok) { toast.success("Pedido rejeitado"); await refresh(); }
      else toast.error(r.error ?? "Erro a rejeitar");
    } finally { setBusyId(null); }
  }

  async function handleDelete(row: PartnershipRow) {
    if (!window.confirm(`Apagar definitivamente o pedido de ${row.name}?`)) return;
    setBusyId(row.id);
    try {
      const r = await deleteFn({ data: { id: row.id } });
      if (r.ok) { toast.success("Pedido apagado"); await refresh(); }
      else toast.error(r.error ?? "Erro a apagar");
    } finally { setBusyId(null); }
  }

  async function handleSaveNotes(row: PartnershipRow, notes: string) {
    setBusyId(row.id);
    try {
      const r = await notesFn({ data: { id: row.id, adminNotes: notes || null } });
      if (r.ok) { toast.success("Notas guardadas"); await refresh(); }
      else toast.error(r.error ?? "Erro");
    } finally { setBusyId(null); }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Mail className="h-5 w-5" /> Pedidos de parceria</h2>
          <p className="text-xs text-muted-foreground">Candidaturas submetidas em <code>/partner</code>. Aprova para criar automaticamente o afiliado, código e link.</p>
        </div>
        <div className="flex gap-1">
          {(["pending", "approved", "rejected", "all"] as const).map((s) => (
            <button key={s} type="button" onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === s ? "border-brand bg-brand text-brand-foreground" : "border-border hover:bg-muted"}`}>
              {s === "pending" ? "Pendentes" : s === "approved" ? "Aprovados" : s === "rejected" ? "Rejeitados" : "Todos"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> A carregar…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum pedido nesta lista.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const open = expanded === r.id;
            return (
              <li key={r.id} className="rounded-xl border border-border">
                <button type="button" onClick={() => setExpanded(open ? null : r.id)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{r.name}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.email}{r.country ? ` · ${r.country}` : ""}{r.followers_approx ? ` · ${r.followers_approx} seguidores` : ""}{r.niche ? ` · ${r.niche}` : ""}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleString("pt-PT")}</div>
                  </div>
                  {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                </button>
                {open ? (
                  <div className="space-y-3 border-t border-border px-4 py-3 text-sm">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Info label="Nome" value={r.name} />
                      <Info label="Email" value={r.email} />
                      <Info label="País" value={r.country} />
                      <Info label="Redes" value={r.social_networks} />
                      <Info label="Instagram" value={r.instagram_url} link />
                      <Info label="TikTok" value={r.tiktok_url} link />
                      <Info label="YouTube" value={r.youtube_url} link />
                      <Info label="Seguidores" value={r.followers_approx} />
                      <Info label="Nicho" value={r.niche} />
                      <Info label="Views/post" value={r.avg_views_per_post} />
                      <Info label="Views/stories" value={r.avg_views_stories} />
                      <Info label="País audiência" value={r.audience_country} />
                      <Info label="Faixa etária" value={r.audience_age_range} />
                      <Info label="Media Kit" value={r.media_kit_url} link />
                      <Info label="Termos aceites" value={r.accepted_terms ? "Sim" : "Não"} />
                    </div>
                    {r.past_partnerships ? (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">Parcerias anteriores</div>
                        <p className="whitespace-pre-wrap text-sm">{r.past_partnerships}</p>
                      </div>
                    ) : null}
                    {r.promotion_plan ? (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">Plano de divulgação</div>
                        <p className="whitespace-pre-wrap text-sm">{r.promotion_plan}</p>
                      </div>
                    ) : null}
                    {r.reason ? (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">Motivo</div>
                        <p className="whitespace-pre-wrap text-sm">{r.reason}</p>
                      </div>
                    ) : null}
                    <NotesEditor initial={r.admin_notes ?? ""} onSave={(v) => handleSaveNotes(r, v)} busy={busyId === r.id} />
                    <div className="flex flex-wrap gap-2 pt-1">
                      {r.status !== "approved" ? (
                        <button type="button" disabled={busyId === r.id} onClick={() => handleApprove(r)}
                          className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground hover:opacity-90 disabled:opacity-60">
                          <Check className="h-3.5 w-3.5" /> Aprovar e criar afiliado
                        </button>
                      ) : null}
                      {r.status !== "rejected" ? (
                        <button type="button" disabled={busyId === r.id} onClick={() => handleReject(r)}
                          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-60">
                          <X className="h-3.5 w-3.5" /> Rejeitar
                        </button>
                      ) : null}
                      <button type="button" disabled={busyId === r.id} onClick={() => handleDelete(r)}
                        className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60">
                        <Trash2 className="h-3.5 w-3.5" /> Apagar
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
  };
  const label = status === "pending" ? "Pendente" : status === "approved" ? "Aprovado" : status === "rejected" ? "Rejeitado" : status;
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${map[status] ?? "bg-muted text-foreground"}`}>{label}</span>;
}

function Info({ label, value, link }: { label: string; value: string | null; link?: boolean }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-brand hover:underline break-all">{value}</a>
      ) : (
        <div className="text-sm break-words">{value}</div>
      )}
    </div>
  );
}

function NotesEditor({ initial, onSave, busy }: { initial: string; onSave: (v: string) => void; busy: boolean }) {
  const [value, setValue] = useState(initial);
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1">Notas internas</div>
      <textarea value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        rows={2} placeholder="Notas visíveis apenas para o fundador/admin" />
      <div className="mt-1 flex justify-end">
        <button type="button" onClick={() => onSave(value)} disabled={busy || value === initial}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50">
          <Save className="h-3 w-3" /> Guardar notas
        </button>
      </div>
    </div>
  );
}
